import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import EmptyState from '@components/Common/EmptyState';
import SearchInput from '@components/Common/SearchInput';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { allAgreements, employers as employersQuery } from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgreementRow, fmtDate } from './agreement';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const AGREEMENT_COLUMNS = ['employer', 'title', 'signedAt', 'expiresAt', 'status', 'shared'];

const PAGE_SIZE = 25;

/*
 * One fetch, then page and filter in memory.
 *
 * The search below reads `rows`, and `rows` used to hold a single server page of 25 — so
 * searching a client whose agreement was on page 2 returned "no results", while the pager kept
 * offering pages 2..N from the *server* total and the header count ignored the search entirely.
 * A filter that quietly sees a twenty-fifth of the data is worse than no filter.
 *
 * 500 rather than unbounded, and the list says so when it is reached rather than silently
 * showing a subset. Free Job Alerts and Subscriptions already take this shape.
 */
const FETCH_SIZE = 500;

/**
 * Every countersigned agreement, across clients.
 *
 * This was the second tab of a page called Contracts, whose first tab managed the templates
 * Mintly generates and emails. Those are two different objects and only one of them is a record
 * of anything: a template is a form, an agreement is what came back signed. The template tab is
 * gone and the page is named after what is left.
 *
 * The per-employer view lives on the employer workspace, which is where an admin goes when they
 * already know whose paperwork they want. This is the other question: what has been signed
 * lately, and which client does that one belong to. It is paginated because it is the only list
 * here that grows without bound — one row per signed document per client, forever.
 *
 * The employer search filters in memory rather than by query. The names are not on the agreement
 * rows at all — they carry an employerId and nothing else — so they have to be resolved
 * client-side regardless, and filtering server-side on a field the server cannot join to would
 * mean paging through ids and hoping. At this size that is the honest trade; if it stops being
 * one, the fix is a name on the row, not a cleverer filter here.
 *
 * "In memory" was aspirational until recently: the page fetched 25 rows at a time and the search
 * filtered those, so it silently searched a twenty-fifth of the data while the pager and the
 * header count still reported the server total. It now fetches once and pages the filtered set.
 */
const Agreements: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AgreementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [employerNames, setEmployerNames] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchGraphQLApiData(allAgreements(0, FETCH_SIZE));
      setRows(data?.getAllAgreements?.content ?? []);
      setTotal(data?.getAllAgreements?.totalElements ?? 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Resolved once. An employer id in a column tells an admin nothing about which client this
    // is, which is the first thing they need from this list.
    let cancelled = false;
    fetchGraphQLApiData(employersQuery(0, 200))
      .then(({ data }) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const e of data?.getAllEmployers?.content ?? []) {
          map[e._id] =
            e?.personalDetails?.fullName ||
            [e?.personalDetails?.firstName, e?.personalDetails?.lastName]
              .filter(Boolean)
              .join(' ') ||
            e?.personalDetails?.email ||
            e._id;
        }
        setEmployerNames(map);
      })
      // A failed lookup degrades to ids rather than blanking the page: the agreements are the
      // point, the names are the annotation.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = employerNames[r.employerId] ?? r.employerId;
      return (
        name.toLowerCase().includes(q) ||
        (r.title ?? '').toLowerCase().includes(q) ||
        (r.fileName ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, employerNames]);

  const body = () => {
    if (loading && rows.length === 0) return <SkeletonLoader rows={10} columns={AGREEMENT_COLUMNS.length} />;

    if (!loading && total === 0) {
      return (
        <EmptyState
          icon={<GavelOutlinedIcon />}
          title="No signed agreements on file"
          body="An agreement arrives by being uploaded against a client, from the Agreements tab on their workspace."
        />
      );
    }

    return (
      <>
        {/* No silent truncation: if the collection outgrows one fetch, the search stops being
            complete and the page has to say so rather than quietly filtering a subset. */}
        {total > FETCH_SIZE && (
          <Typography className="agr-cap-notice">
            {`Showing the ${FETCH_SIZE} most recent of ${total} agreements. Search covers those only.`}
          </Typography>
        )}
        <MintlyTable
          pagination={{
            enabled: true,
            page,
            rowsPerPage: PAGE_SIZE,
            // The filtered set, so the pager cannot offer a page the search has emptied.
            totalNumberOfRows: visible.length,
            handleChangePage: (_e: unknown, next: number) => setPage(next),
          }}
          rows={visible
            .slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
            .map((r) => ({ ...r, id: r._id }))}
          selectors={AGREEMENT_COLUMNS}
          onRowClick={(row: AgreementRow) => navigate(`/employers/${row.employerId}`)}
          tableHeadRow={{
            employer: <>Employer</>,
            title: <>Agreement</>,
            signedAt: <>Signed</>,
            expiresAt: <>Expires</>,
            status: <>Status</>,
            shared: <>Employer can see</>,
          }}
          tableBodyRow={{
            employer: (row: AgreementRow) => (
              <>{employerNames[row.employerId] ?? row.employerId}</>
            ),
            title: (row: AgreementRow) => <>{row.title || row.fileName || '-'}</>,
            signedAt: (row: AgreementRow) => <>{fmtDate(row.signedAt)}</>,
            expiresAt: (row: AgreementRow) => <>{row.expiresAt ? fmtDate(row.expiresAt) : '-'}</>,
            status: (row: AgreementRow) => <CRMStatusChip status={row.status || 'ACTIVE'} />,
            shared: (row: AgreementRow) => (
              <>{row.visibleToEmployer ? 'Yes' : 'No'}</>
            ),
          }}
        />
      </>
    );
  };

  return (
    <CRMPageTemplate
      icon={<GavelOutlinedIcon />}
      title="Agreements"
      totalCount={visible.length}
      /* Through the template rather than a FilterRow of our own. CLAUDE.md's filter rule is that
         instant, in-memory filtering uses FilterRow — and `inlineFilters` renders one, so the
         page gets the shared row without also being the second place that decides its layout. */
      inlineFilters={(
        <SearchInput
          value={search}
          onChange={(v: string) => { setSearch(v); setPage(0); }}
          placeholder="Employer, title or file name"
        />
      )}
    >
      {body()}
    </CRMPageTemplate>
  );
};

export default Agreements;
